import { ArrowDown, ArrowDownLeft, ArrowUp, ArrowUpRight, Minus, Wallet } from 'lucide-react';
import styles from './CashFlow.module.css';
import BlankState from '../BlankState/BlankState';
import Meter from '../Meter/Meter';
import { fmtEUR } from '../../../utils/moneyFormat';

// Money in and money out, read as one movement rather than two unrelated
// cards. The halves are mirrored — eyebrow, figure, bar, note, quiet total —
// so the eye compares collection against spend without any extra chrome.
const clampPct = (n) => Math.max(0, Math.min(100, Number(n) || 0));

const CashFlow = ({ earnings, spend, period, loading }) => {
  const hasData = Boolean(earnings || spend);
  const inPct = clampPct(earnings?.progress);
  const outPct = clampPct(spend?.progress);
  const collected = Number(earnings?.periodTotal) || 0;
  const spent = Number(spend?.period) || 0;
  const diff = Number(spend?.diff) || 0;
  const prevLabel = period?.previous?.label || 'the previous period';
  const sameStretch = period?.isToDate ? ` of ${prevLabel} to date` : ` of ${prevLabel}`;

  const inCaption = () => {
    if (!collected) return 'Nothing collected in this period';
    if (!earnings?.prevTotal) return `Nothing collected in ${prevLabel}`;
    if (inPct >= 100) return `Ahead of ${prevLabel}`;
    return `${Math.round(inPct)}%${sameStretch}`;
  };

  // Spending less than the previous period is the good direction, so the pill
  // colours are deliberately inverted against the revenue convention.
  const renderMovement = () => {
    if (diff === 0) {
      return (
        <span className="dshPill dshPillMute">
          <Minus size={10} strokeWidth={3} />
          level with {prevLabel}
        </span>
      );
    }
    const up = diff > 0;
    const Arrow = up ? ArrowUp : ArrowDown;
    return (
      <span
        className={`dshPill ${up ? 'dshPillNeg' : 'dshPillPos'}`}
        title={`${fmtEUR(Math.abs(diff))} ${up ? 'more' : 'less'} than the comparable stretch of ${prevLabel}`}
      >
        <Arrow size={10} strokeWidth={3} />
        <span className="dshNum">{`${fmtEUR(Math.abs(diff))} ${up ? 'more' : 'less'}`}</span>
      </span>
    );
  };

  // The shared meter, so money-in and money-out are drawn with exactly the
  // same instrument as every other proportion on the dashboard.
  const renderBar = (pct, tone, label) => (
    <Meter pct={pct} tone={tone} size="sm" label={label} className={styles.bar} />
  );

  return (
    <div className={`dshPanel ${styles.root}`}>
      <div className="dshHead">
        <div>
          <h3 className="dshTitle">Cash Flow</h3>
          <p className="dshSub dshTrunc">
            Money in and out · {period?.label || 'this month'}
          </p>
        </div>
      </div>

      {!loading && !hasData ? (
        <BlankState
          icon={Wallet}
          title="No cash movement in this period"
          text="Collections from clients and payouts to suppliers will be summarised here."
        />
      ) : (
        <div className={styles.halves}>
          <section className={styles.half}>
            <div className={styles.halfHead}>
              <span className="dshChip dshChipPos">
                <ArrowDownLeft size={15} strokeWidth={2.3} />
              </span>
              <span className="dshEyebrow">Collected</span>
            </div>

            {loading ? (
              <span className={`dshSkel ${styles.skelFigure}`} />
            ) : (
              <div className={`dshNum ${styles.figure}`}>{fmtEUR(collected)}</div>
            )}

            {loading ? (
              <span className={`dshSkel ${styles.skelBar}`} />
            ) : (
              renderBar(inPct, 'pos', `Collections against ${prevLabel}`)
            )}

            {loading ? (
              <span className={`dshSkel ${styles.skelNote}`} />
            ) : (
              <p className={styles.note}>{inCaption()}</p>
            )}

            <div className={`dshRow ${styles.foot}`}>
              <span className={styles.footLabel}>Today</span>
              {loading ? (
                <span className={`dshSkel ${styles.skelFootValue}`} />
              ) : (
                <b className={`dshNum ${styles.footValue}`}>{fmtEUR(earnings?.today ?? 0)}</b>
              )}
            </div>
          </section>

          <section className={styles.half}>
            <div className={styles.halfHead}>
              <span className="dshChip dshChipBrand">
                <ArrowUpRight size={15} strokeWidth={2.3} />
              </span>
              <span className="dshEyebrow">Spent</span>
            </div>

            {loading ? (
              <span className={`dshSkel ${styles.skelFigure}`} />
            ) : (
              <div className={`dshNum ${styles.figure}`}>{fmtEUR(spent)}</div>
            )}

            {loading ? (
              <span className={`dshSkel ${styles.skelBar}`} />
            ) : (
              renderBar(outPct, 'brand', `Spend against ${prevLabel}`)
            )}

            {loading ? (
              <span className={`dshSkel ${styles.skelNote}`} />
            ) : (
              <div className={styles.note}>{renderMovement()}</div>
            )}

            <div className={`dshRow ${styles.foot}`}>
              <span className={`dshTrunc ${styles.footLabel}`}>{prevLabel}</span>
              {loading ? (
                <span className={`dshSkel ${styles.skelFootValue}`} />
              ) : (
                <b className={`dshNum ${styles.footValue}`}>{fmtEUR(spend?.prevToDate ?? 0)}</b>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default CashFlow;
